import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Loader2, ImageOff, ImageDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/StatusPill";
import { api, apiError } from "@/lib/api";

const PLATFORMS = [
  { id: "instagram", label: "Instagram Feed" },
  { id: "instagram_story", label: "Instagram Story" },
  { id: "facebook", label: "Facebook" },
  { id: "whatsapp_status", label: "WhatsApp Status" },
  { id: "google_business", label: "Google Business" },
  { id: "tiktok", label: "TikTok / Reel" },
];
const OCCASIONS = [
  { id: "neu_eingetroffen", label: "Neu eingetroffen" },
  { id: "fahrzeug_der_woche", label: "Fahrzeug der Woche" },
  { id: "preis_reduziert", label: "Preis reduziert" },
  { id: "verkauft", label: "Verkauft" },
];

export default function ContentPage() {
  const [assets, setAssets] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [occasion, setOccasion] = useState("neu_eingetroffen");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/content").then((r) => setAssets(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  const generate = async () => {
    if (!vehicleId) return toast.warning("Bitte Fahrzeug auswählen");
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicleId}/content/generate`, { platform, occasion });
      toast.success("Social-Media-Entwurf erstellt (wird nicht automatisch veröffentlicht)");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (a) => {
    const text = `${a.caption}\n\n${(a.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  };

  const [fmtBusy, setFmtBusy] = useState(null);
  const downloadFormats = async (a) => {
    setFmtBusy(a.id);
    try {
      const r = await api.get(`/content/${a.id}/image-formats`);
      const f = r.data.formats || {};
      const base = (a.vehicle_label || "fahrzeug").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const dl = (dataUrl, name) => {
        const el = document.createElement("a");
        el.href = dataUrl;
        el.download = name;
        document.body.appendChild(el);
        el.click();
        el.remove();
      };
      if (f.feed) dl(f.feed, `${base}-feed-1080x1080.jpg`);
      if (f.story) setTimeout(() => dl(f.story, `${base}-story-1080x1920.jpg`), 400);
      toast.success("Post-Bilder heruntergeladen: 1:1 (Feed) + 9:16 (Story/Reel)");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setFmtBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold">Content Studio</h1>
        <p className="text-sm text-muted-foreground">Social-Media-Entwürfe aus der Fahrzeugakte. Veröffentlichung erst nach Ihrer Freigabe.</p>
      </div>

      <Card data-tour="content-generator">
        <CardContent className="pt-5 flex flex-wrap gap-3 items-end">
          <div className="space-y-1.5 min-w-[220px]">
            <span className="text-xs text-muted-foreground">Fahrzeug</span>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger data-testid="content-vehicle-select"><SelectValue placeholder="Fahrzeug wählen..." /></SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.marke} {v.modell} ({v.bestandsnummer})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Plattform</span>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="w-[180px]" data-testid="content-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Anlass</span>
            <Select value={occasion} onValueChange={setOccasion}>
              <SelectTrigger className="w-[180px]" data-testid="content-occasion-select"><SelectValue /></SelectTrigger>
              <SelectContent>{OCCASIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={generate} disabled={busy} className="gap-2 h-10" data-testid="content-generate-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Entwurf erstellen
          </Button>
        </CardContent>
      </Card>

      {!assets ? (
        <div className="grid md:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-56" />)}</div>
      ) : assets.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Noch keine Entwürfe. Erstellen Sie oben den ersten Social-Media-Entwurf.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="content-assets-grid">
          {assets.map((a) => (
            <Card key={a.id} className="overflow-hidden">
              <CardHeader className="pb-2 flex-row items-center gap-3 space-y-0">
                {a.cover_image_url ? (
                  <img src={a.cover_image_url} alt="" className="h-10 w-14 rounded object-cover" loading="lazy" />
                ) : (
                  <div className="h-10 w-14 rounded bg-secondary flex items-center justify-center"><ImageOff className="h-4 w-4 text-muted-foreground" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-sm truncate">{a.vehicle_label}</CardTitle>
                  <div className="flex gap-1.5 mt-1">
                    <StatusPill label={PLATFORMS.find((p) => p.id === a.platform)?.label || a.platform} color="bg-sky-500/15 text-sky-300 border-sky-500/30" />
                    <StatusPill label="Entwurf" color="bg-slate-500/20 text-slate-300 border-slate-500/30" />
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => copy(a)} data-testid={`content-copy-${a.id}`} title="Kopieren">
                  <Copy className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {a.hook && <p className="font-medium">{a.hook}</p>}
                <p className="text-muted-foreground whitespace-pre-line text-xs max-h-36 overflow-y-auto scrollbar-thin">{a.caption}</p>
                <p className="text-xs text-primary/80 break-words">{(a.hashtags || []).join(" ")}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 mt-1"
                  onClick={() => downloadFormats(a)}
                  disabled={fmtBusy === a.id}
                  data-testid={`content-formats-${a.id}`}
                >
                  {fmtBusy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageDown className="h-3.5 w-3.5" />}
                  Post-Bilder laden (1:1 Feed + 9:16 Story)
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
